"use client";

import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    quoteKey: "about.testimonial1",
    name: "Carlos Mendoza",
    roleKey: "about.roleTeamCaptain",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&crop=face",
  },
  {
    quoteKey: "about.testimonial2",
    name: "María González",
    roleKey: "about.roleVenueManager",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=96&h=96&fit=crop&crop=face",
  },
  {
    quoteKey: "about.testimonial3",
    name: "Diego Rodríguez",
    roleKey: "about.roleRegularPlayer",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=96&h=96&fit=crop&crop=face",
  },
  {
    quoteKey: "about.testimonial4",
    name: "Ana Silva",
    roleKey: "about.roleEventOrganizer",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=96&h=96&fit=crop&crop=face",
  },
] as const;

const AUTO_MS = 6500;

export default function AboutTestimonialsCarousel() {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = testimonials.length;

  const go = useCallback(
    (dir: -1 | 1) => {
      setIndex((i) => (i + dir + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (prefersReducedMotion || paused) return;
    const id = globalThis.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, AUTO_MS);
    return () => globalThis.clearInterval(id);
  }, [paused, prefersReducedMotion, count]);

  const current = testimonials[index];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50/40 to-white py-12 sm:py-16 md:py-24 lg:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border-soft to-transparent" aria-hidden />
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <span className="inline-block max-w-[min(100%,28rem)] rounded-full bg-primary-soft px-3 py-1.5 text-xs font-semibold text-primary sm:px-4 sm:text-sm">
            {t("about.trustedFeedback")}
          </span>
          <h2 className="mt-3 text-balance px-1 text-xl font-bold uppercase tracking-tight text-slate-900 sm:mt-4 sm:text-2xl md:text-3xl lg:text-4xl">
            {t("about.customerInsights")}
          </h2>
        </div>

        <div
          className="relative mx-auto mt-8 w-full max-w-3xl sm:mt-12 sm:max-w-3xl md:mt-14"
          role="region"
          aria-roledescription="carousel"
          aria-label={t("about.customerInsights")}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div className="relative mx-auto w-full">
            <AnimatePresence mode="wait" initial={false}>
              <motion.article
                key={current.quoteKey}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, y: -12 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col rounded-2xl border border-border-soft bg-white p-5 shadow-[0_4px_24px_-4px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/4 sm:p-7 md:p-9"
              >
                <Quote
                  className="h-8 w-8 shrink-0 text-primary/25 sm:h-9 sm:w-9 md:h-10 md:w-10"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <p className="mt-3 text-pretty text-sm leading-relaxed text-slate-700 sm:mt-4 sm:text-base md:text-lg">
                  {t(current.quoteKey)}
                </p>
                <footer className="mt-5 flex flex-col gap-4 border-t border-slate-100 pt-5 sm:mt-6 sm:flex-row sm:items-center sm:gap-4 sm:pt-6">
                  <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
                    <Image
                      src={current.image}
                      alt=""
                      width={48}
                      height={48}
                      sizes="(max-width: 640px) 48px, 52px"
                      loading="lazy"
                      className="h-12 w-12 shrink-0 rounded-full object-cover ring-2 ring-white shadow-md sm:h-[52px] sm:w-[52px]"
                    />
                    <div className="min-w-0">
                      <p className="break-words text-sm font-semibold text-slate-900 sm:text-base">{current.name}</p>
                      <p className="text-xs text-slate-500 sm:text-sm">{t(current.roleKey)}</p>
                    </div>
                  </div>
                  <div
                    className="flex justify-end gap-0.5 sm:ml-auto sm:shrink-0"
                    aria-hidden
                  >
                    {Array.from({ length: current.rating }).map((_, i) => (
                      <span key={i} className="text-base text-amber-400 sm:text-lg">
                        ★
                      </span>
                    ))}
                  </div>
                </footer>
              </motion.article>
            </AnimatePresence>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:mt-8 sm:gap-4 md:mt-10">
            <button
              type="button"
              onClick={() => go(-1)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border-soft bg-white text-slate-700 shadow-sm transition hover:border-primary/30 hover:bg-primary-soft hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label={t("about.testimonialPrev")}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2" role="group" aria-label={t("about.testimonialDots")}>
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-current={i === index ? "true" : undefined}
                  aria-label={t("about.testimonialGoTo", { n: i + 1 })}
                  onClick={() => setIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    i === index ? "w-8 bg-primary" : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => go(1)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border-soft bg-white text-slate-700 shadow-sm transition hover:border-primary/30 hover:bg-primary-soft hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              aria-label={t("about.testimonialNext")}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <p key={index} className="sr-only" aria-live="polite">
            {t("about.testimonialSlideStatus", { current: index + 1, total: count })}
          </p>
        </div>
      </div>
    </section>
  );
}
